// ╔══════════════════════════════════════════════════════════════╗
// ║  DateRangePicker — Pills + custom range (from/to)              ║
// ║                                                                ║
// ║  Usage:                                                        ║
// ║    <DateRangePicker value={{from, to}} onChange={setRange} /> ║
// ╚══════════════════════════════════════════════════════════════╝

import { useEffect, useState } from 'react';
import Icon from './Icon.jsx';
import { Pills } from './ui.jsx';

const PRESETS = [
  { value: 'today', label: 'اليوم' },
  { value: 'week', label: 'هذا الأسبوع' },
  { value: 'month', label: 'هذا الشهر' },
  { value: 'quarter', label: '3 شهور' },
  { value: 'year', label: 'هذه السنة' },
  { value: 'custom', label: 'فترة مخصصة' },
];

export default function DateRangePicker({ value, onChange, presets = PRESETS }) {
  const [preset, setPreset] = useState('month');
  const [custom, setCustom] = useState({ from: '', to: '' });

  // Compute date range from preset
  useEffect(() => {
    if (preset === 'custom') return;
    const now = new Date();
    let from, to;
    switch (preset) {
      case 'today': {
        const t = now.toISOString().slice(0, 10);
        from = t;
        to = t;
        break;
      }
      case 'week': {
        const start = new Date(now);
        start.setDate(now.getDate() - now.getDay());
        from = start.toISOString().slice(0, 10);
        to = now.toISOString().slice(0, 10);
        break;
      }
      case 'month': {
        from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
        to = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        break;
      }
      case 'quarter': {
        const start = new Date(now);
        start.setMonth(now.getMonth() - 2);
        start.setDate(1);
        from = start.toISOString().slice(0, 10);
        to = now.toISOString().slice(0, 10);
        break;
      }
      case 'year': {
        from = `${now.getFullYear()}-01-01`;
        to = `${now.getFullYear()}-12-31`;
        break;
      }
      default:
        return;
    }
    onChange?.({ from, to, preset });
  }, [preset]);

  // Apply custom on change
  function applyCustom() {
    if (!custom.from || !custom.to) return;
    onChange?.({ from: custom.from, to: custom.to, preset: 'custom' });
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Icon name="cal" size={14} className="text-ink-400" />
      <Pills value={preset} onChange={setPreset} items={presets} />
      {preset === 'custom' && (
        <div className="flex items-center gap-1.5">
          <input
            type="date"
            value={custom.from}
            onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))}
            className="h-9 px-2 rounded-lg bg-ink-50 dark:bg-ink-800 ring-1 ring-ink-200 dark:ring-ink-700 text-xs"
          />
          <span className="text-ink-400 text-xs">→</span>
          <input
            type="date"
            value={custom.to}
            onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))}
            className="h-9 px-2 rounded-lg bg-ink-50 dark:bg-ink-800 ring-1 ring-ink-200 dark:ring-ink-700 text-xs"
          />
          <button
            onClick={applyCustom}
            disabled={!custom.from || !custom.to}
            className="h-9 px-3 rounded-lg bg-brand-500 text-ink-950 text-xs font-bold disabled:opacity-50"
          >
            تطبيق
          </button>
        </div>
      )}
      {value?.from && (
        <span className="text-[11px] text-ink-400 font-mono">
          {value.from} → {value.to}
        </span>
      )}
    </div>
  );
}
